import type { FastifyRequest } from 'fastify'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'


/**
 * Central AI quota limits by plan tier.
 * Keep in sync with billing plan limits to avoid double-quotas.
 */
export const AI_QUOTA_LIMITS = {
  free: { daily: 5, monthly: 50 },
  premium: { daily: 50, monthly: 1000 },
  pro: { daily: 200, monthly: 5000 }
} as const

export type PlanTier = keyof typeof AI_QUOTA_LIMITS

function mapStringToPlanTier(value: string): PlanTier {
  const normalized = value.trim().toLowerCase()
  if (normalized === 'free') return 'free'
  if (normalized.includes('premium')) return 'premium'
  if (normalized.includes('pro')) return 'pro'
  return 'free'
}

export function normalizePlanTier(planValue: unknown): PlanTier {
  if (isString(planValue)) {
    return mapStringToPlanTier(planValue)
  }

  if (planValue && typeof planValue === 'object') {
    const tierCandidate = (planValue as Record<string, unknown>).tier
    if (isString(tierCandidate)&& tierCandidate in AI_QUOTA_LIMITS) {
      return tierCandidate as PlanTier
    }

    const idCandidate = (planValue as Record<string, unknown>).id
    if (isNonEmptyString(idCandidate)) {
      return mapStringToPlanTier(idCandidate)
    }

    const nameCandidate = (planValue as Record<string, unknown>).name
    if (isNonEmptyString(nameCandidate)) {
      return mapStringToPlanTier(nameCandidate)
    }
  }

  return 'free'
}

export function resolveUserPlanTier(request: FastifyRequest): PlanTier {
  const planValue = (request.user as any)?.plan
  return normalizePlanTier(planValue)
}

export type AIQuotaWindow = {
  used: number
  limit: number
  remaining: number
  period: 'day' | 'month'
  periodEnd?: string
}

export type AIQuotaSnapshot = {
  plan: PlanTier
  daily: AIQuotaWindow
  monthly: AIQuotaWindow
}

export function buildAIQuotaSnapshot(
  plan: PlanTier,
  stats: { dailyCount: number; monthlyCount: number },
  now = new Date()
): AIQuotaSnapshot {
  const limits = AI_QUOTA_LIMITS[plan]

  const nextDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0))
  const monthEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0, 0))

  const dailyRemaining = Math.max(0, limits.daily - stats.dailyCount)
  const monthlyRemaining = Math.max(0, limits.monthly - stats.monthlyCount)

  return {
    plan,
    daily: {
      used: stats.dailyCount,
      limit: limits.daily,
      remaining: dailyRemaining,
      period: 'day',
      periodEnd: nextDay.toISOString()
    },
    monthly: {
      used: stats.monthlyCount,
      limit: limits.monthly,
      remaining: monthlyRemaining,
      period: 'month',
      periodEnd: monthEnd.toISOString()
    }
  }
}
